<template>
    <div ref="dom" class="InfiniteScroll">
        <slot />
    </div>
</template>

<script lang="ts">
    import throttle from 'lodash/throttle';
    import { Component, Prop, Ref, Vue } from 'vue-property-decorator';

    // PRODUCT-26110 Task 13 (Finding 4): declares its own `$emit('scroll')` call below
    // -- without a declared `emits`, Vue 3 warns "Component emitted event 'scroll' but
    // it is neither declared in the emits option nor as an 'onScroll' prop" every time
    // it fires (seen x31 in one QA session).
    @Component({
        emits: [ 'scroll' ],
    })
    export default class InfiniteScroll extends Vue {
        private unsubscribe: (() => void)[] = [];
        private recalculateListener = () => this.recalculate();
        private interval: any;
        private scheduleRecalculation = throttle(() => this.recalculate(), 10);

        @Prop({ type: Number, default: 95 })
        private emitAt!: number;

        @Ref()
        private readonly dom!: HTMLDivElement;

        private updated() {
            this.recalculate();
        }

        private addListeners() {
            this.addListener(window, 'scroll');
            this.addListener(window, 'resize');
        }

        private addListener<T extends Element | Window>(element: T, eventName: Parameters<T['addEventListener']>[0]) {
            element.addEventListener(eventName, this.recalculateListener);
            this.unsubscribe.push(() => element.removeEventListener(eventName, this.recalculateListener));
        }

        private getScrolledPercentage() {
            const rect = this.dom.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

            const endsOn = rect.top + rect.height;
            const pixelsLeftToScroll = Math.max(0, endsOn - viewportHeight);
            const scrolledPart = (rect.height - pixelsLeftToScroll) / rect.height;
            return 100 * scrolledPart;
        }

        private recalculate() {
            if (this.getScrolledPercentage() >= this.emitAt) {
                this.$emit('scroll');
            }
        }

        private mounted() {
            // Regular listeners
            this.addListeners();

            // Fallback in case something goes wrong with detection
            this.interval = setInterval(() => this.scheduleRecalculation(), 500);
        }

        private unmounted() {
            clearInterval(this.interval);
            this.scheduleRecalculation.cancel();
            this.unsubscribe.forEach((fn) => fn());
            this.unsubscribe = [];
        }
    }
</script>
